import { Clock, Trophy } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import giftImg from '../../../../assets/pages/rewards/gift.png';
import { CONTEST_RULES } from '../rewardsPageConstants';
import type { DrawCountdown } from '../rewardsPageTypes';
import type { ContestStats } from '../hooks/useContestStats';
import type { ContestLeaderboardItem, ContestLeaderboardResponse } from '../../../../helpers/utils/types';
import { formatCurrency } from '../../../../helpers/utils/utils';
import { sanitizeRentDropTerminology } from '../../../../helpers/utils/rentDropTerminology';

export const ContestSection = (props: {
  drawCountdown: DrawCountdown;
  tickets: number;
  contestStats?: ContestStats;
  contestStatsLoading?: boolean;
  contestLeaderboard?: ContestLeaderboardItem[];
  contestLeaderboardLoading?: boolean;
  contestLeaderboardMeta?: ContestLeaderboardResponse['meta'];
}) => {
  const {
    drawCountdown,
    tickets,
    contestLeaderboard,
    contestLeaderboardLoading,
  } = props;

  const hasWinners = (contestLeaderboard?.length ?? 0) > 0;

  return (
    <div className='mt-6 space-y-6'>
      <div className='relative overflow-hidden rounded-[18px] bg-gradient-to-r from-[#0055FF] to-[#003BB5] px-7 py-8 text-white shadow-[0_14px_45px_rgba(166,177,203,0.35)]'>
        {/* confetti */}
        {[
          { top: '10%', left: '6%', w: 6, h: 14, r: -18, o: 0.8 },
          { top: '14%', left: '18%', w: 5, h: 10, r: 22, o: 0.7 },
          { top: '8%', left: '36%', w: 6, h: 12, r: 8, o: 0.75 },
          { top: '18%', left: '54%', w: 5, h: 11, r: -10, o: 0.7 },
          { top: '9%', left: '72%', w: 6, h: 13, r: 16, o: 0.8 },
          { top: '16%', left: '88%', w: 5, h: 10, r: -22, o: 0.7 },
        ].map((c, i) => (
          <div
            key={i}
            className='absolute rounded-md bg-[#F6C546]'
            style={{
              top: c.top,
              left: c.left,
              width: c.w,
              height: c.h,
              transform: `rotate(${c.r}deg)`,
              opacity: c.o,
            }}
            aria-hidden
          />
        ))}

        {/* gifts */}
        <img
          src={giftImg}
          alt='Gifts'
          className='pointer-events-none select-none absolute left-[-10px] bottom-[-30px] w-[140px] opacity-95 hidden sm:block'
          style={{ transform: 'rotate(4deg)' }}
        />
        <img
          src={giftImg}
          alt='Gifts'
          className='pointer-events-none select-none absolute left-[70px] bottom-[-42px] w-[110px] opacity-85 hidden sm:block'
          style={{ transform: 'rotate(-10deg)' }}
        />
        <img
          src={giftImg}
          alt='Gifts'
          className='pointer-events-none select-none absolute right-[-8px] bottom-[-30px] w-[140px] opacity-95 hidden sm:block'
          style={{ transform: 'rotate(-4deg)' }}
        />
        <img
          src={giftImg}
          alt='Gifts'
          className='pointer-events-none select-none absolute right-[74px] bottom-[-42px] w-[110px] opacity-85 hidden sm:block'
          style={{ transform: 'rotate(10deg)' }}
        />

        <div className='relative z-10 text-center'>
          <span className='inline-flex items-center justify-center px-5 py-2 rounded-full border border-white/60 bg-white/10 text-sm font-medium'>
            Monthly rent drop
          </span>
          <div className='mt-5 text-[28px] leading-[34px] sm:text-[32px] sm:leading-[38px] font-semibold tracking-tight'>
            Free rent contest. We&apos;re not kidding.
          </div>
          <div className='mt-2 text-sm text-white/85'>
            200+ winners every month. Grand rent drop: a full month of free rent.
          </div>

          <div className='mt-6 flex items-center justify-center gap-3 flex-wrap'>
            {[
              { v: drawCountdown.days, l: 'Days' },
              { v: drawCountdown.hours, l: 'Hours' },
              { v: drawCountdown.minutes, l: 'Mins' },
              { v: drawCountdown.seconds, l: 'Secs' },
            ].map((t) => (
              <div
                key={t.l}
                className='bg-white/10 rounded-[16px] px-5 py-4 min-w-[74px] backdrop-blur-[10px]'
              >
                <div className='text-[32px] leading-none font-semibold text-[#F6C546] tabular-nums'>
                  {String(t.v).padStart(2, '0')}
                </div>
                <div className='mt-1 text-[10px] uppercase tracking-[0.1em] font-semibold text-[#D7DEF3]'>
                  {t.l}
                </div>
              </div>
            ))}
          </div>

          <div className='mt-6 inline-flex items-center justify-center rounded-[12px] px-6 py-3 border border-[#F6C546]/90 bg-[#003BB5]/20'>
            <span className='text-sm font-semibold'>
              You have <span className='text-[#F6C546] text-lg font-semibold'>{tickets}</span>{' '}
              entries this month
            </span>
          </div>
        </div>
      </div>

      <div className='space-y-6'>
        <div className='grid grid-cols-1 xl:grid-cols-12 gap-6'>
          <div className='xl:col-span-7'>
            <h2 className='text-lg font-semibold text-[#677285] mb-4'>Winner list</h2>
            <div className='flex flex-col gap-3'>
              {contestLeaderboardLoading ? (
                <div className='rounded-[18px] border border-[#E9EDF5] bg-white p-5 shadow-[0_12px_35px_rgba(166,177,203,0.16)] text-sm text-[#677285]'>
                  Loading leaderboard…
                </div>
              ) : (contestLeaderboard?.length ?? 0) > 0 ? (
                contestLeaderboard!.map((row, idx) => {
                  const displayRank = row.awardRank ?? idx + 1;
                  const displayName = row.firstName?.trim() || 'Anonymous Winner';

                  return (
                    <div
                      key={row.contestWinnerId}
                      className='rounded-[18px] border border-[#E9EDF5] bg-white p-4 shadow-[0_12px_35px_rgba(166,177,203,0.16)] flex items-center justify-between gap-4'
                    >
                      <div className='flex items-center gap-3 min-w-0'>
                        <div
                          className='w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0 shadow-[0_10px_22px_rgba(33,81,236,0.18)]'
                          style={{
                            background:
                              'linear-gradient(180deg, rgba(33,81,236,0.14) 0%, rgba(33,81,236,0.06) 100%)',
                            border: '1px solid rgba(33,81,236,0.18)',
                          }}
                          aria-label={`Rank ${displayRank}`}
                        >
                          <span className='text-[var(--primary-color)] font-semibold tabular-nums'>
                            {displayRank}
                          </span>
                        </div>
                        <div className='min-w-0'>
                          <div className='font-semibold text-main truncate'>{displayName}</div>
                          <div className='text-xs text-[#8A92AB] mt-0.5 truncate'>
                            {sanitizeRentDropTerminology(row.prizeName)} · {row.prizeTypeName}
                          </div>
                        </div>
                      </div>
                      <div className='text-sm font-semibold text-[var(--primary-color)] shrink-0 text-right tabular-nums'>
                        {formatCurrency(row.prizeAmount)}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className='rounded-[18px] border border-[#E9EDF5] bg-white p-6 shadow-[0_12px_35px_rgba(166,177,203,0.16)]'>
                  <div className='flex items-start gap-4'>
                    <div className='w-12 h-12 rounded-[14px] bg-[#EEF5FF] flex items-center justify-center shrink-0'>
                      <Trophy size={20} className='text-[var(--primary-color)]' aria-hidden />
                    </div>
                    <div className='min-w-0'>
                      <div className='font-semibold text-main'>No winners posted yet</div>
                      <div className='mt-1 text-sm text-[#677285]'>
                        Once the draw is completed, winners will appear here. In the meantime, keep
                        earning tickets and you’ll be ready for the next draw.
                      </div>
                    </div>
                  </div>

                  <div className='mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3'>
                    {[
                      {
                        label: 'Next draw',
                        value: `${drawCountdown.days}d ${drawCountdown.hours}h`,
                      },
                      { label: 'Your entries', value: String(tickets) },
                      { label: 'Tip', value: 'Visit “How to earn”' },
                    ].map((row) => (
                      <div
                        key={row.label}
                        className='rounded-[14px] border border-[#EEF2F7] bg-[#FAFBFF] px-4 py-3'
                      >
                        <div className='text-xs font-semibold text-[#8A92AB]'>{row.label}</div>
                        <div className='mt-1 text-sm font-semibold text-main'>{row.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='xl:col-span-5 mt-10 space-y-6 self-start'>
            <div className='rounded-[18px] border border-[#E9EDF5] bg-white p-6 shadow-[0_12px_35px_rgba(166,177,203,0.14)]'>
              <h3 className='text-[16px] font-semibold text-main flex items-center gap-2'>
                <span className='inline-flex w-6 h-6 rounded-full bg-[#EEF5FF] items-center justify-center text-[var(--primary-color)] text-xs'>
                  i
                </span>
                Contest rules
              </h3>
              <div className='mt-4 flex flex-col gap-3'>
                {CONTEST_RULES.map((row) => (
                  <div
                    key={row}
                    className='flex items-start gap-3 py-2 border-b border-[#EEF2F7] last:border-b-0'
                  >
                    <span className='text-[var(--primary-color)] shrink-0 leading-[18px] mt-[1px]'>
                      •
                    </span>
                    <span className='text-[13px] text-[#677285] leading-snug'>{row}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {hasWinners && (
          <div>
            <h3 className='text-lg font-semibold text-[#677285] mb-4'>
              Real Canadians, real winners
            </h3>
            <Swiper
              className='rewards-winners-swiper'
              modules={[Navigation, Autoplay]}
              navigation
              autoplay={{ delay: 2600, disableOnInteraction: false, pauseOnMouseEnter: true }}
              speed={650}
              loop={(contestLeaderboard?.length ?? 0) > 3}
              grabCursor
              spaceBetween={16}
              slidesPerView={1.15}
              breakpoints={{
                640: { slidesPerView: 1.6 },
                1024: { slidesPerView: 2.2 },
                1280: { slidesPerView: 2.8 },
              }}
            >
              {(contestLeaderboard ?? []).map((row: ContestLeaderboardItem) => {
                const displayName = row.firstName?.trim() || 'Anonymous Winner';
                const rentDropLabel =
                  row.prizeTypeName?.toLowerCase() === 'ticket'
                    ? `${Number(row.prizeAmount).toLocaleString('en-US')} Tickets`
                    : formatCurrency(row.prizeAmount);

                return (
                  <SwiperSlide key={row.contestWinnerId} className='pb-2'>
                    <article className='rounded-[18px] border border-[#E9EDF5] bg-white p-6 shadow-[0_12px_35px_rgba(166,177,203,0.14)] min-h-[168px] transition-shadow hover:shadow-[0_14px_30px_rgba(166,177,203,0.22)]'>
                      <div className='w-[56px] h-[56px] rounded-[16px] bg-[linear-gradient(180deg,#2B45C9_0%,#1A2F8F_100%)] flex items-center justify-center shadow-[0_12px_26px_rgba(33,81,236,0.22)] shrink-0'>
                        <span className='text-[26px] leading-none text-white' aria-hidden>
                          🎉
                        </span>
                      </div>

                      <h4 className='mt-4 font-semibold text-[18px] leading-[24px] text-main'>
                        {displayName}
                      </h4>
                      <p className='mt-1 text-[13px] leading-[18px] text-[#8A92AB]'>
                        {sanitizeRentDropTerminology(row.prizeName)}
                      </p>

                      <div className='mt-4 flex flex-wrap items-center gap-2'>
                        <span className='inline-flex items-center gap-2 rounded-full bg-[#EEF5FF] px-4 py-2 text-[13px] font-semibold text-[var(--primary-color)]'>
                          <span className='inline-flex items-center justify-center w-7 h-7 rounded-full bg-white shadow-sm'>
                            <Trophy size={16} className='text-[var(--primary-color)]' />
                          </span>
                          {rentDropLabel}
                        </span>
                        <span className='inline-flex items-center gap-2 rounded-full bg-[#F3F5FA] px-4 py-2 text-[13px] font-semibold text-[#677285]'>
                          <span className='inline-flex items-center justify-center w-7 h-7 rounded-full bg-white shadow-sm'>
                            <Clock size={16} className='text-[#677285]' />
                          </span>
                          {row.prizeTypeName}
                        </span>
                      </div>
                    </article>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        )}
      </div>
    </div>
  );
};
